/**
 * Specialty Icon Resolver
 * 
 * v133.21.0: Centralized icon lookup for specialty display.
 * 
 * Priority:
 * 1. Bundled icon by specialty ID (the 11 current icons)
 * 2. Bundled icon by specialty iconKey (if set in questionnaire data)
 * 3. null (caller renders a fallback like a colored circle)
 * 
 * This allows the admin panel to assign existing icon keys to new specialties
 * without a patient app deploy. The bundled assets serve as the icon library.
 */

import eyeIcon from '../assets/icons/eye2x.png'                       // v230.4.0 new anatomical eye
import earIcon from '../assets/icons/ear2x.png'                       // v230.4.0 new anatomical ear
import noseIcon from '../assets/icons/nose2x.png'                     // v230.4.0 new nose & sinus head
import throatIcon from '../assets/icons/larynx.png'                     // v229.0.0 larynx replaces throat_logo.svg
import headNeckIcon from '../assets/icons/neck.png'                     // v229.0.0 anatomical neck replaces head_and_neck_logo.svg
import stomachIcon from '../assets/icons/stomach2x.png'              // v230.4.3 new stomach & gut
import urinaryIcon from '../assets/icons/urinary2x.png'              // v230.4.3 new urinary
import muscleIcon from '../assets/icons/muscle_joints.png'              // v229.0.0 knee-joint replaces muscle_and_joints.svg
import skinIcon from '../assets/icons/skin2x.png'                     // v230.4.0 new skin
import painIcon from '../assets/icons/chronic_pain.png'                 // v229.0.0 flame replaces pain_logo.svg
import wellnessFigureIcon from '../assets/icons/wellness_logo.svg'      // existing person-with-checkmark figure
import wellnessChecksIcon from '../assets/icons/wellness_checks_logo.png' // v213.10 new blood-drop-cross PNG
// v229.0.0 — canonical-specialty home cards
import heartIcon from '../assets/icons/heart.png'
import lungsIcon from '../assets/icons/lungs2x.png'
import kidneyIcon from '../assets/icons/kidney2x.png'
import bloodIcon from '../assets/icons/blood.png'
import uterusIcon from '../assets/icons/uterus2x.png'
import brainIcon from '../assets/icons/brain2x.png'
import mentalIcon from '../assets/icons/mentalhealth.png'
import vascularIcon from '../assets/icons/vascular.png'
import infectionIcon from '../assets/icons/infection.png'
import sleepIcon from '../assets/icons/sleep.png'
// v234.6.0 — Podiatry + Dental canonical-specialty home cards
import feetIcon from '../assets/icons/feet.png'
import teethIcon from '../assets/icons/teeth.png'

// All bundled icons keyed by known IDs
const ICON_LIBRARY = {
  'eye': eyeIcon,
  'ear': earIcon,
  'nose': noseIcon,
  'throat': throatIcon,
  'head-neck': headNeckIcon,
  'stomach-gut': stomachIcon,
  'stomach': stomachIcon,
  'urinary': urinaryIcon,
  'muscle-joints': muscleIcon,
  'muscle': muscleIcon,
  'skin': skinIcon,
  'pain': painIcon,
  // v213.10: rebrand. Existing person+checkmark figure now keyed as
  // 'wellness-figure' and used by the new General specialty. The
  // Health & Wellness specialty (renamed to "Wellness Checks") now uses
  // the new blood-drop+cross PNG keyed as 'wellness-checks'.
  'wellness-figure': wellnessFigureIcon,
  'wellness-checks': wellnessChecksIcon,
  // v229.0.0 — canonical-specialty home cards
  'heart': heartIcon,
  'lungs': lungsIcon,
  'kidney': kidneyIcon,
  'blood': bloodIcon,
  'uterus': uterusIcon,
  'brain': brainIcon,
  'mental': mentalIcon,
  'vascular': vascularIcon,
  'infection': infectionIcon,
  'sleep': sleepIcon,
  // v234.6.0 — Podiatry + Dental
  'feet': feetIcon,
  'teeth': teethIcon,
  // Backward-compat aliases — older case data or saved app state may still
  // reference these keys. Resolve them sensibly so we don't render blank icons.
  'health-wellness': wellnessChecksIcon, // specialty id; falls through to new icon
  'wellness': wellnessChecksIcon,
  'head': headNeckIcon,
}

/**
 * Resolve the icon source for a specialty.
 * @param {object} specialty - Specialty object from questionnaire data
 * @returns {string|null} - Image source URL or null
 */
export function getSpecialtyIcon(specialty) {
  if (!specialty) return null
  // 1. Try specialty.iconUrl (admin-uploaded custom icon — full URL)
  if (specialty.iconUrl && specialty.iconUrl.startsWith('http')) {
    return specialty.iconUrl
  }
  // 2. Try specialty.icon (admin-assigned icon key from bundled library)
  if (specialty.icon && ICON_LIBRARY[specialty.icon]) {
    return ICON_LIBRARY[specialty.icon]
  }
  // 3. Try specialty.id (default match for existing specialties)
  if (specialty.id && ICON_LIBRARY[specialty.id]) {
    return ICON_LIBRARY[specialty.id]
  }
  // 4. No match — caller shows fallback
  return null
}


export { ICON_LIBRARY }
